/**
 * HTTP 请求封装
 * 基于 Fetch API 实现，支持请求拦截、响应拦截、错误处理
 */

interface RequestConfig extends RequestInit {
  params?: Record<string, any>;
  timeout?: number;
}

interface ResponseData<T = any> {
  code: number;
  message: string;
  data: T;
}

/**
 * 请求拦截器
 */
const requestInterceptor = (config: RequestConfig): RequestConfig => {
  // 添加 token（如果存在）
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    (config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  // 添加请求时间戳（防止缓存）
  if (config.params) {
    config.params._t = Date.now();
  }

  return config;
};

/**
 * 响应拦截器
 */
const responseInterceptor = async <T = any>(
  response: Response,
  config: RequestConfig
): Promise<ResponseData<T>> => {
  const data = await response.json();

  // 处理 HTTP 状态码
  if (!response.ok) {
    throw new Error(data.message || data.msg || `请求失败: ${response.status}`);
  }

  // 处理业务状态码（兼容 msg/message 字段）
  if (data.code !== 200) {
    throw new Error(data.message || data.msg || '请求失败');
  }

  return data;
};

/**
 * 错误拦截器
 */
const errorInterceptor = (error: Error, config: RequestConfig): never => {
  console.error('请求错误:', error);

  // 网络错误
  if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
    throw new Error('网络连接失败，请检查网络设置');
  }

  // 超时错误
  if (error.message.includes('timeout')) {
    throw new Error('请求超时，请稍后重试');
  }

  throw error;
};

/**
 * 构建 URL 参数
 */
const buildUrl = (url: string, params?: Record<string, any>): string => {
  if (!params) return url;

  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

  return queryString ? `${url}?${queryString}` : url;
};

/**
 * 超时控制
 */
const withTimeout = (promise: Promise<Response>, timeout?: number): Promise<Response> => {
  if (!timeout) return promise;

  return Promise.race([
    promise,
    new Promise<Response>((_, reject) =>
      setTimeout(() => reject(new Error('timeout')), timeout)
    ),
  ]);
};

/**
 * 核心请求方法
 */
const request = async <T = any>(
  url: string,
  config: RequestConfig = {}
): Promise<ResponseData<T>> => {
  try {
    // 检测是否为 FormData，如果是则不应手动设置 Content-Type
    const isFormData = config.body instanceof FormData;
    
    // 请求拦截
    const finalConfig = requestInterceptor({
      ...config,
      headers: isFormData 
        ? { ...config.headers }  // FormData 不设置 Content-Type，让浏览器自动设置 multipart/form-data
        : {
            'Content-Type': 'application/json',
            ...config.headers,
          },
    });

    // 构建完整 URL
    const fullUrl = buildUrl(url, finalConfig.params);

    // 发起请求
    const response = await withTimeout(fetch(fullUrl, finalConfig), finalConfig.timeout);

    // 响应拦截
    return await responseInterceptor<T>(response, finalConfig);
  } catch (error) {
    // 错误拦截
    return errorInterceptor(error as Error, config);
  }
};

/**
 * GET 请求
 */
export const get = <T = any>(
  url: string,
  params?: Record<string, any>,
  config?: RequestConfig
): Promise<ResponseData<T>> => {
  return request<T>(url, { ...config, method: 'GET', params });
};

/**
 * POST 请求（支持 JSON 和 FormData）
 */
export const post = <T = any>(
  url: string,
  data?: any,
  config?: RequestConfig
): Promise<ResponseData<T>> => {
  // 如果 data 是 FormData，直接使用，否则 JSON 序列化
  const body = data instanceof FormData ? data : JSON.stringify(data);
  
  return request<T>(url, {
    ...config,
    method: 'POST',
    body,
  });
};

/**
 * PUT 请求
 */
export const put = <T = any>(
  url: string,
  data?: any,
  config?: RequestConfig
): Promise<ResponseData<T>> => {
  return request<T>(url, {
    ...config,
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * DELETE 请求
 */
export const del = <T = any>(
  url: string,
  config?: RequestConfig
): Promise<ResponseData<T>> => {
  return request<T>(url, { ...config, method: 'DELETE' });
};

/**
 * 文件上传（专用方法，兼容旧代码）
 */
export const upload = <T = any>(
  url: string,
  file: File,
  config?: Omit<RequestConfig, 'body' | 'headers'>
): Promise<ResponseData<T>> => {
  const formData = new FormData();
  formData.append('file', file);

  return request<T>(url, {
    ...config,
    method: 'POST',
    body: formData,
  });
};

/**
 * 下载文件
 */
export const download = async (
  url: string,
  filename?: string,
  config?: RequestConfig
): Promise<void> => {
  const response = await fetch(url, config);
  const blob = await response.blob();

  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename || 'download';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);
};

export default request;